import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "https://fenny.se/filmproduktion-mellansverige";

const REGION_CITIES = [
  { label: "Stockholm", path: "/filmproduktion/stockholm" },
  { label: "Uppsala", path: "/filmproduktion/uppsala" },
  { label: "Västerås", path: "/filmproduktion/vasteras" },
  { label: "Örebro", path: "/filmproduktion/orebro" },
  { label: "Linköping", path: "/filmproduktion/linkoping" },
  { label: "Eskilstuna", path: "/employer-branding-film/eskilstuna" },
  { label: "Gävle", path: "/employer-branding-film/gavle" },
];

const FAQ = [
  {
    q: "Vad räknas till Mellansverige?",
    a: "I praktiken Stockholms län, Uppsala, Västmanland, Södermanland, Östergötland, Örebro, Värmland, Dalarna och Gävleborg. Fenny producerar regelbundet i alla dessa områden från våra team i Stockholm och Västerås.",
  },
  {
    q: "Vilka städer i Mellansverige har Fenny lokala team?",
    a: "Stockholm är vårt huvudteam. Västerås har lokal närvaro för Mälardalen. Övriga orter (Uppsala, Örebro, Eskilstuna, Linköping, Gävle, Falun, Karlstad) bokas via närmaste team — restid ingår transparent i offerten.",
  },
  {
    q: "Vad kostar filmproduktion i Mellansverige?",
    a: "Samma prislogik som i Stockholm. Social-content från 60 000 kr, reklamfilm från 150 000 kr, brand-film från 250 000 kr. Pulse-abonnemang från 14 995 kr/mån oavsett ort.",
  },
  {
    q: "Hur snabbt är ni på plats?",
    a: "Mälardalen och Uppsala-regionen: inom 5 arbetsdagar. Övriga Mellansverige: 5–10 arbetsdagar beroende på säsong.",
  },
];

export const Route = createFileRoute("/filmproduktion-mellansverige")({
  head: () => ({
    meta: [
      { title: "Filmproduktion Mellansverige — lokala team | Fenny" },
      {
        name: "description",
        content:
          "Filmproduktion i Mellansverige med team i Stockholm och Västerås. Reklamfilm, brand-film, kundcase, employer branding — fast pris, leverans 4–6 v. Pulse-abonnemang från 14 995 kr/mån.",
      },
      { property: "og:title", content: "Filmproduktion Mellansverige | Fenny" },
      {
        property: "og:description",
        content:
          "Premiumfilm i Mälardalen, Stockholm-regionen och övriga Mellansverige — abonnemang eller projekt.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PATH },
    ],
    links: [{ rel: "canonical", href: PATH }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmproduktion-mellansverige", name: "Filmproduktion Mellansverige — lokala team & nationell leverans | Fenny", description: "Filmproduktion i Mellansverige med team i Stockholm och Västerås. Reklamfilm, brand-film, kundcase, employer branding — fast pris, leverans 4–6 v. Pulse-abonnemang från 14 995 kr/mån."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Filmproduktion Mellansverige",
            path: PATH,
            description:
              "Fenny levererar filmproduktion i Mellansverige från team i Stockholm och Västerås. Reklamfilm, brand-film, employer branding och kundcase.",
            serviceType: "Filmproduktion",
            priceRange: "Från 14 995 kr/mån (Pulse) eller 60 000 kr/projekt",
            areaServed: [
              "Stockholm",
              "Uppsala",
              "Västmanland",
              "Södermanland",
              "Östergötland",
              "Örebro län",
              "Värmland",
              "Dalarna",
              "Gävleborg",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Filmproduktion", path: "/filmproduktion-pa-abonnemang" },
            { name: "Mellansverige", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: RegionHub,
});

function RegionHub() {
  return (
    <>
      <Section>
        <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Mellansverige
        </div>
        <h1 className="text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
          Filmproduktion i Mellansverige — Mälardalen, Stockholm-regionen och övriga
        </h1>
        <QuickAnswer
          text="Fenny producerar film i hela Mellansverige från team i Stockholm och Västerås. Vi producerar i Stockholms län, Uppsala, Västmanland, Södermanland, Östergötland, Örebro, Värmland, Dalarna och Gävleborg — med fast pris, fast leveranstid och samma kvalitet som i storstadsproduktion."
        />
      </Section>

      <Section>
        <h2 className="text-3xl font-semibold md:text-4xl">Städer i Mellansverige</h2>
        <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {REGION_CITIES.map((c) => (
            <li key={c.path}>
              <Link
                to={c.path}
                className="group flex items-center justify-between rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-sm transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
              >
                <span>{c.label}</span>
                <ArrowRight className="size-4 opacity-50 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      <Section>
        <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
        <div className="mt-8 space-y-6 max-w-3xl">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/70">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <SignatureCta />
    </>
  );
}

